import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import i18next from 'i18next'
import { I18nextProvider, useTranslation, initReactI18next } from 'react-i18next'
import { DEFAULT_LANG, SUPPORTED_LANGS, localizePath, langFromPath, stripLang } from './locale'
import strings from './strings'

const RESOURCES = Object.fromEntries(
    Object.entries(strings).map(([lang, translation]) => [lang, { translation }])
)

// One instance per language, not a singleton: prerendering renders every route
// in a single process, so a mutable global language leaks between routes.
const instances = new Map()

export const getI18n = (lang = DEFAULT_LANG) => {
    const key = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG

    if (!instances.has(key)) {
        const instance = i18next.createInstance()
        instance.use(initReactI18next).init({
            lng: key,
            fallbackLng: DEFAULT_LANG,
            resources: RESOURCES,
            interpolation: { escapeValue: false },
            react: { useSuspense: false },
        })
        instances.set(key, instance)
    }

    return instances.get(key)
}

export { localizePath, langFromPath, stripLang }

export const useLang = () => useTranslation().i18n.language

export const LangProvider = ({ children }) => (
    <I18nextProvider i18n={getI18n(langFromPath(useLocation().pathname))}>
        {children}
    </I18nextProvider>
)

export const FixedLangProvider = ({ lang, children }) => (
    <I18nextProvider i18n={getI18n(lang)}>{children}</I18nextProvider>
)

export const LocalizedLink = ({ to, ...props }) => (
    <Link to={localizePath(to, useLang())} {...props} />
)

export { useTranslation }
export { Trans } from 'react-i18next'
